'use client';

import {
  createContext,
  type ReactElement,
  type ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { useConvexAuth, useQuery } from 'convex/react';
import usePresence, { type PresenceAPI } from '@convex-dev/presence/react';
import { useParams } from 'next/navigation';

import { api } from '@/convex/_generated/api';
import type { Id } from '@/convex/_generated/dataModel';

// The component times a session out after 2.5x this interval.
const PRESENCE_INTERVAL_MS = 30_000;

const PRESENCE_API: PresenceAPI = {
  heartbeat: api.users.activity.presenceHeartbeat,
  list: api.users.activity.presenceList,
  // Must be `presence:disconnect`: the hook's tab-close beacon hard-codes
  // that path, so in-page and beacon disconnects share one function.
  disconnect: api.presence.disconnect,
};

type WorkspacePresence = {
  /** Users with IMAI open in this workspace (null while unknown). */
  onlineUserIds: ReadonlySet<string> | null;
};

const EMPTY_PRESENCE: WorkspacePresence = { onlineUserIds: null };
const WorkspacePresenceContext =
  createContext<WorkspacePresence>(EMPTY_PRESENCE);

/** Users currently online in the workspace (null while unknown). */
export function useWorkspaceOnlineUserIds(): ReadonlySet<string> | null {
  return useContext(WorkspacePresenceContext).onlineUserIds;
}

type OrganizationPresence = {
  organizationId: Id<'organizations'>;
  presence: WorkspacePresence;
};

/** Holds this browser's presence session for one workspace room. */
function WorkspacePresenceSession({
  organizationId,
  onChange,
}: {
  organizationId: Id<'organizations'>;
  onChange: (next: OrganizationPresence) => void;
}): null {
  // The server records presence for the authenticated user, so the hook's
  // userId only scopes this browser session.
  const state = usePresence(
    PRESENCE_API,
    organizationId,
    'self',
    PRESENCE_INTERVAL_MS
  );
  useEffect(() => {
    onChange({
      organizationId,
      presence: {
        onlineUserIds: state
          ? new Set(state.filter((entry) => entry.online).map((e) => e.userId))
          : null,
      },
    });
  }, [onChange, organizationId, state]);
  return null;
}

/**
 * Workspace-wide presence for every workspace route, via the
 * @convex-dev/presence component: one room per workspace, heartbeats while
 * IMAI is open, a graceful disconnect when the tab closes.
 */
export function WorkspacePresenceProvider({
  children,
}: {
  children: ReactNode;
}): ReactElement {
  const params = useParams<{ orgSlug?: string }>();
  const orgSlug = params?.orgSlug;
  const { isAuthenticated } = useConvexAuth();
  const organization = useQuery(
    api.organizations.queries.getBySlug,
    isAuthenticated && orgSlug ? { slug: orgSlug } : 'skip'
  );
  const [reported, setReported] = useState<OrganizationPresence | null>(null);
  const organizationId = organization?._id;
  const presence =
    reported && reported.organizationId === organizationId
      ? reported.presence
      : EMPTY_PRESENCE;

  // The session is a sibling, never a wrapper: server-rendered workspace
  // pages mount before the workspace lookup returns, and switching the
  // wrapper element when it does would remount the whole page.
  return (
    <WorkspacePresenceContext.Provider value={presence}>
      {organizationId ? (
        <WorkspacePresenceSession
          key={organizationId}
          organizationId={organizationId}
          onChange={setReported}
        />
      ) : null}
      {children}
    </WorkspacePresenceContext.Provider>
  );
}
